import { Box, BoxProps, Stack } from "@mui/material";
import { useMemo } from "react";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import Datetime from "@/common/components/Datetime/Datetime";
import VehicleLink from "@/common/components/Entity/Vehicle/VehicleLink";
import { ImageComparisonWorkspace } from "@/common/components/Images/Comparison/ImageComparisonWorkspace";
import { FileItem } from "@/common/fileItem";
import { VisualInspectionHelper } from "@/common/helpers/entity/visualInspection";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto } from "@/core/api/generated/v1";

import VisualInspectionLink from "../../VisualInspection/VisualInspectionLink";
import ContractLink from "../ContractLink";

export interface ContractVisualInspectionComparisonProps {
  contract: ContractDto;
  sx?: BoxProps["sx"];
}

export function ContractVisualInspectionComparison({
  contract,
  sx,
}: ContractVisualInspectionComparisonProps) {
  const checkOutViId = contract.checkOutSpotInfo?.visualInspectionId || undefined;
  const checkInViId = contract.checkInSpotInfo?.visualInspectionId || undefined;
  const isDataAvailable = !!checkOutViId && !!checkInViId;

  const checkOutViRequest = useApiRequest(
    apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      visualInspectionId: checkOutViId || "",
    },
    {
      deps: [checkOutViId],
      skip: !checkOutViId,
    },
  );
  const checkOutVi = checkOutViRequest.data;

  const checkInViRequest = useApiRequest(
    apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      visualInspectionId: checkInViId || "",
    },
    {
      deps: [checkInViId],
      skip: !checkInViId,
    },
  );
  const checkInVi = checkInViRequest.data;

  const beforeImages = useMemo(
    () =>
      checkOutVi
        ? FileItem.createManyFrom(VisualInspectionHelper.getAllAttachments(checkOutVi)).filter(
            (x) => x.fileType.isImage,
          )
        : [],
    [checkOutVi],
  );
  const afterImages = useMemo(
    () =>
      checkInVi
        ? FileItem.createManyFrom(VisualInspectionHelper.getAllAttachments(checkInVi)).filter(
            (x) => x.fileType.isImage,
          )
        : [],
    [checkInVi],
  );

  const topSlot = (
    <Stack spacing={0.5}>
      <Stack sx={{ alignItems: "flex-start" }}>
        <VehicleLink entity={contract.vehicle} inlineProps={{ withMenu: true }} />
        <ContractLink entity={contract} inlineProps={{ withMenu: true }} />
      </Stack>
    </Stack>
  );

  return (
    <Box sx={sx}>
      {!isDataAvailable && <NoDataAlert title='No visual inspections to compare.' />}

      {/* {isEnabled && (beforeImages.length === 0 || afterImages.length === 0) && (
        <Box sx={{ mb: 2 }}>
          <NoDataAlert />
        </Box>
      )} */}

      {isDataAvailable && (
        <ImageComparisonWorkspace
          topSlot={topSlot}
          before={{
            title: "Check out",
            subtitle: (
              <VisualInspectionLink
                entityId={checkOutVi?.id}
                entity={checkOutVi}
                inlineProps={{ withTooltip: true, withMenu: true }}
              />
            ),
            images: beforeImages,
            label: (
              // Clients request to not show before/after labels as their customers confuse whether it's before/after check out/in..
              <Box>
                <Datetime datetime={checkOutVi?.inspectedAt} />
              </Box>
            ),
          }}
          after={{
            title: "Check in",
            subtitle: (
              <VisualInspectionLink
                entityId={checkInVi?.id}
                entity={checkInVi}
                inlineProps={{ withTooltip: true, withMenu: true }}
              />
            ),
            images: afterImages,
            label: (
              <Box>
                <Datetime datetime={checkInVi?.inspectedAt} />
              </Box>
            ),
          }}
        />
      )}
    </Box>
  );
}
